import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Store, Plus, Loader2, Package, Inbox, Trash2 } from 'lucide-react';

const emptyCatalogo = () => ({
  tipo_equipamento_id: '',
  titulo: '',
  descricao: '',
  fabricante: '',
  modelo: '',
  capacidade_valor: '',
  capacidade_unidade: 'kg/h',
  preco_referencia: '',
  prazo_entrega_dias: 30,
});

const emptyCotacao = () => ({
  preco_total: '',
  prazo_entrega_dias: '',
  fabricante: '',
  modelo: '',
  capacidade_valor: '',
  capacidade_unidade: 'kg/h',
  condicoes_pagamento: '',
  observacoes: '',
  catalogo_item_id: '',
});

const FornecedorEquipamentosPage = () => {
  const { getEmpresaId } = useAuth();
  const fornecedorId = getEmpresaId();
  const [tab, setTab] = useState('catalogo');
  const [catalogo, setCatalogo] = useState([]);
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalCat, setModalCat] = useState(false);
  const [editCatId, setEditCatId] = useState(null);
  const [formCat, setFormCat] = useState(emptyCatalogo());
  const [selSol, setSelSol] = useState(null);
  const [formCot, setFormCot] = useState(emptyCotacao());
  const [msg, setMsg] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [rCat, rSol, rTipos] = await Promise.all([
        httpsCallable(functions, 'listCatalogoEquipamentoFornecedor')({
          fornecedor_id: fornecedorId,
          apenas_ativos: false,
        }),
        httpsCallable(functions, 'listSolicitacoesCotacaoEquipamentoParaFornecedor')({}),
        httpsCallable(functions, 'listTiposEquipamento')({ apenas_ativos: true }),
      ]);
      setCatalogo(rCat.data?.data?.items || []);
      setSolicitacoes(rSol.data?.data?.items || []);
      setTipos(rTipos.data?.data?.items || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [fornecedorId]);

  const salvarCatalogo = async (e) => {
    e.preventDefault();
    try {
      await httpsCallable(functions, 'upsertCatalogoEquipamentoFornecedor')({
        id: editCatId || undefined,
        ...formCat,
        capacidade_valor: Number(formCat.capacidade_valor) || 0,
        preco_referencia: Number(formCat.preco_referencia) || 0,
        prazo_entrega_dias: Number(formCat.prazo_entrega_dias) || 30,
      });
      setModalCat(false);
      setMsg('Catálogo atualizado.');
      await load();
    } catch (ex) {
      alert(ex.message || 'Erro ao salvar');
    }
  };

  const removerCatalogo = async (id) => {
    if (!window.confirm('Remover item do catálogo?')) return;
    try {
      await httpsCallable(functions, 'deleteCatalogoEquipamentoFornecedor')({ id });
      await load();
    } catch (ex) {
      alert(ex.message || 'Erro');
    }
  };

  const enviarCotacao = async (e) => {
    e.preventDefault();
    if (!selSol) return;
    try {
      await httpsCallable(functions, 'registrarCotacaoEquipamento')({
        solicitacao_id: selSol.id,
        ...formCot,
        preco_total: Number(formCot.preco_total),
        prazo_entrega_dias: Number(formCot.prazo_entrega_dias),
        capacidade_valor: Number(formCot.capacidade_valor) || undefined,
        catalogo_item_id: formCot.catalogo_item_id || undefined,
      });
      setSelSol(null);
      setFormCot(emptyCotacao());
      setMsg('Cotação enviada.');
      await load();
    } catch (ex) {
      alert(ex.message || 'Erro ao enviar cotação');
    }
  };

  const abrirResponder = (sol) => {
    setSelSol(sol);
    const catMatch = catalogo.find((c) => c.tipo_equipamento_id === sol.tipo_equipamento_id);
    setFormCot({
      ...emptyCotacao(),
      fabricante: catMatch?.fabricante || '',
      modelo: catMatch?.modelo || '',
      capacidade_valor: catMatch?.capacidade_valor || '',
      capacidade_unidade: catMatch?.capacidade_unidade || 'kg/h',
      prazo_entrega_dias: catMatch?.prazo_entrega_dias || '',
      catalogo_item_id: catMatch?.id || '',
    });
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Store className="h-7 w-7 text-blue-600" />
          Marketplace de Equipamentos
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Publique itens no catálogo (tipos globais QualiManager) e responda solicitações de cotação das fábricas.
        </p>
      </div>

      {msg && <p className="text-green-700 text-sm bg-green-50 p-3 rounded-lg">{msg}</p>}

      <div className="flex gap-2 border-b">
        <button
          type="button"
          onClick={() => setTab('catalogo')}
          className={`px-4 py-2 text-sm font-medium border-b-2 ${
            tab === 'catalogo' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600'
          }`}
        >
          <Package className="h-4 w-4 inline mr-1" />
          Meu catálogo
        </button>
        <button
          type="button"
          onClick={() => setTab('solicitacoes')}
          className={`px-4 py-2 text-sm font-medium border-b-2 ${
            tab === 'solicitacoes' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600'
          }`}
        >
          <Inbox className="h-4 w-4 inline mr-1" />
          Solicitações ({solicitacoes.filter((s) => !s.ja_respondido).length})
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-10 w-10 animate-spin text-blue-500" />
        </div>
      ) : tab === 'catalogo' ? (
        <>
          <button
            type="button"
            onClick={() => {
              setEditCatId(null);
              setFormCat(emptyCatalogo());
              setModalCat(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center gap-2"
          >
            <Plus className="h-5 w-5" />
            Novo item
          </button>
          <div className="grid gap-3">
            {catalogo.length === 0 ? (
              <p className="text-gray-500">Nenhum item publicado.</p>
            ) : (
              catalogo.map((c) => (
                <div
                  key={c.id}
                  className={`border rounded-xl p-4 flex justify-between gap-3 ${c.ativo === false ? 'opacity-50' : ''}`}
                >
                  <div>
                    <p className="font-semibold">{c.titulo}</p>
                    <p className="text-sm text-gray-600">{c.tipo_nome}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {c.fabricante} {c.modelo} — {c.capacidade_valor} {c.capacidade_unidade}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="text-sm text-blue-600"
                      onClick={() => {
                        setEditCatId(c.id);
                        setFormCat({
                          tipo_equipamento_id: c.tipo_equipamento_id,
                          titulo: c.titulo,
                          descricao: c.descricao || '',
                          fabricante: c.fabricante || '',
                          modelo: c.modelo || '',
                          capacidade_valor: c.capacidade_valor || '',
                          capacidade_unidade: c.capacidade_unidade || 'kg/h',
                          preco_referencia: c.preco_referencia || '',
                          prazo_entrega_dias: c.prazo_entrega_dias || 30,
                        });
                        setModalCat(true);
                      }}
                    >
                      Editar
                    </button>
                    {c.ativo !== false && (
                      <button type="button" onClick={() => removerCatalogo(c.id)} className="text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      ) : (
        <div className="space-y-3">
          {solicitacoes.length === 0 ? (
            <p className="text-gray-500">Nenhuma solicitação no momento.</p>
          ) : (
            solicitacoes.map((s) => (
              <div key={s.id} className="border rounded-xl p-4">
                <p className="font-semibold">{s.tipo_equipamento_nome}</p>
                <p className="text-sm text-gray-600">{s.descricao_necessidade || '—'}</p>
                <p className="text-xs text-gray-500 mt-1">Empresa: {s.empresa_id?.slice?.(0, 12)}…</p>
                {s.ja_respondido ? (
                  <span className="text-sm text-green-700 mt-2 inline-block">Cotação já enviada</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => abrirResponder(s)}
                    className="mt-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm"
                  >
                    Responder
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {modalCat && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="font-bold mb-4">{editCatId ? 'Editar' : 'Novo'} item do catálogo</h3>
            <form onSubmit={salvarCatalogo} className="space-y-3 text-sm">
              <label className="block font-medium">
                Tipo (global) *
                <select
                  className="w-full border rounded-lg mt-1 px-3 py-2"
                  value={formCat.tipo_equipamento_id}
                  onChange={(e) => setFormCat((f) => ({ ...f, tipo_equipamento_id: e.target.value }))}
                  required
                >
                  <option value="">Selecione…</option>
                  {tipos.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nome}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block font-medium">
                Título *
                <input
                  className="w-full border rounded-lg mt-1 px-3 py-2"
                  value={formCat.titulo}
                  onChange={(e) => setFormCat((f) => ({ ...f, titulo: e.target.value }))}
                  required
                />
              </label>
              <label className="block font-medium">
                Descrição
                <textarea
                  className="w-full border rounded-lg mt-1 px-3 py-2"
                  rows={2}
                  value={formCat.descricao}
                  onChange={(e) => setFormCat((f) => ({ ...f, descricao: e.target.value }))}
                />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block font-medium">
                  Fabricante
                  <input
                    className="w-full border rounded-lg mt-1 px-3 py-2"
                    value={formCat.fabricante}
                    onChange={(e) => setFormCat((f) => ({ ...f, fabricante: e.target.value }))}
                  />
                </label>
                <label className="block font-medium">
                  Modelo
                  <input
                    className="w-full border rounded-lg mt-1 px-3 py-2"
                    value={formCat.modelo}
                    onChange={(e) => setFormCat((f) => ({ ...f, modelo: e.target.value }))}
                  />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="block font-medium">
                  Capacidade
                  <input
                    type="number"
                    className="w-full border rounded-lg mt-1 px-3 py-2"
                    value={formCat.capacidade_valor}
                    onChange={(e) => setFormCat((f) => ({ ...f, capacidade_valor: e.target.value }))}
                  />
                </label>
                <label className="block font-medium">
                  Unidade
                  <input
                    className="w-full border rounded-lg mt-1 px-3 py-2"
                    value={formCat.capacidade_unidade}
                    onChange={(e) => setFormCat((f) => ({ ...f, capacidade_unidade: e.target.value }))}
                  />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="block font-medium">
                  Preço ref. (R$)
                  <input
                    type="number"
                    className="w-full border rounded-lg mt-1 px-3 py-2"
                    value={formCat.preco_referencia}
                    onChange={(e) => setFormCat((f) => ({ ...f, preco_referencia: e.target.value }))}
                  />
                </label>
                <label className="block font-medium">
                  Prazo entrega (dias)
                  <input
                    type="number"
                    className="w-full border rounded-lg mt-1 px-3 py-2"
                    value={formCat.prazo_entrega_dias}
                    onChange={(e) => setFormCat((f) => ({ ...f, prazo_entrega_dias: e.target.value }))}
                  />
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setModalCat(false)} className="px-4 py-2 border rounded-lg">
                  Cancelar
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg">
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selSol && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <h3 className="font-bold mb-2">Cotar: {selSol.tipo_equipamento_nome}</h3>
            <p className="text-sm text-gray-600 mb-4">{selSol.descricao_necessidade}</p>
            <form onSubmit={enviarCotacao} className="space-y-3 text-sm">
              <label className="block font-medium">
                Preço total (R$) *
                <input
                  type="number"
                  step="0.01"
                  className="w-full border rounded-lg mt-1 px-3 py-2"
                  value={formCot.preco_total}
                  onChange={(e) => setFormCot((f) => ({ ...f, preco_total: e.target.value }))}
                  required
                />
              </label>
              <label className="block font-medium">
                Prazo entrega (dias) *
                <input
                  type="number"
                  className="w-full border rounded-lg mt-1 px-3 py-2"
                  value={formCot.prazo_entrega_dias}
                  onChange={(e) => setFormCot((f) => ({ ...f, prazo_entrega_dias: e.target.value }))}
                  required
                />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block font-medium">
                  Fabricante
                  <input
                    className="w-full border rounded-lg mt-1 px-3 py-2"
                    value={formCot.fabricante}
                    onChange={(e) => setFormCot((f) => ({ ...f, fabricante: e.target.value }))}
                  />
                </label>
                <label className="block font-medium">
                  Modelo
                  <input
                    className="w-full border rounded-lg mt-1 px-3 py-2"
                    value={formCot.modelo}
                    onChange={(e) => setFormCot((f) => ({ ...f, modelo: e.target.value }))}
                  />
                </label>
              </div>
              <label className="block font-medium">
                Condições de pagamento
                <input
                  className="w-full border rounded-lg mt-1 px-3 py-2"
                  value={formCot.condicoes_pagamento}
                  onChange={(e) => setFormCot((f) => ({ ...f, condicoes_pagamento: e.target.value }))}
                />
              </label>
              <label className="block font-medium">
                Observações
                <textarea
                  className="w-full border rounded-lg mt-1 px-3 py-2"
                  rows={2}
                  value={formCot.observacoes}
                  onChange={(e) => setFormCot((f) => ({ ...f, observacoes: e.target.value }))}
                />
              </label>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setSelSol(null)} className="px-4 py-2 border rounded-lg">
                  Cancelar
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg">
                  Enviar cotação
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FornecedorEquipamentosPage;
